import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from 'react-router-dom';
import { DocumentTextIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useUserProfile } from '../UserProfileContext';
import { useCountUp } from '../hooks/useCountUp';

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function formatUsd(value: number, withCents = false): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: withCents ? 2 : 0,
    maximumFractionDigits: withCents ? 2 : 0,
  });
}

type DashboardHeroProps = {
  name: string;
  invoiceCount: number;
  invoiceTotal: number;
  showInvoices: boolean;
};

/**
 * DAG glow-up masthead — a serif greeting over a charcoal/gold panel with
 * real, model-derived figures (never fabricated). Mounted only under the flag,
 * so the count-up hooks stay dormant otherwise.
 */
const DashboardHero: React.FC<DashboardHeroProps> = ({ name, invoiceCount, invoiceTotal, showInvoices }) => {
  const animatedCount = useCountUp(invoiceCount);
  const animatedTotal = useCountUp(invoiceTotal);

  return (
    <header className="dag-hero">
      <div className="dag-hero__aurora" aria-hidden="true" />
      <div className="dag-hero__lead">
        <p className="dag-hero__eyebrow">Digital Family Office</p>
        <h1 className="dag-hero__title">
          {timeOfDayGreeting()}
          {name && (
            <>
              , <span className="dag-hero__accent">{name}</span>
            </>
          )}
        </h1>
        <p className="dag-hero__sub">Your family office, at a glance.</p>
      </div>
      {showInvoices && (
        <div className="dag-hero__stats">
          <div className="dag-hero__stat">
            <div className="dag-hero__stat-num">{Math.round(animatedCount)}</div>
            <div className="dag-hero__stat-label">Open Invoices</div>
          </div>
          <div className="dag-hero__stat">
            <div className="dag-hero__stat-num dag-amount">{formatUsd(animatedTotal)}</div>
            <div className="dag-hero__stat-label">Total Due</div>
          </div>
        </div>
      )}
    </header>
  );
};

interface Invoice {
  totalAmount?: number;
}

interface InvoicesApiResponse {
  success: boolean;
  message?: string;
  errors: string[];
  invoices?: {
    results: Invoice[];
    total: number;
    start: number;
    max: number;
  };
}

const DashboardPage: React.FC = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { profile, isLoading: isLoadingProfile } = useUserProfile();
  const [invoiceCount, setInvoiceCount] = useState<number>(0);
  const [invoiceTotal, setInvoiceTotal] = useState<number>(0);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const displayName = profile?.displayName || "";

  useEffect(() => {
    const fetchInvoiceSummary = async () => {
      // Wait for profile to load first
      if (isLoadingProfile) {
        return;
      }

      // Only fetch invoices if the user has a Bill.com customer ID
      if (!profile?.hasBillComCustomerId) {
        setInvoiceCount(0);
        setInvoiceTotal(0);
        setLoadingInvoices(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();

        // Fetch invoices
        const invoicesResponse = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5010"}/api/invoice/getinvoices`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!invoicesResponse.ok) {
          if (invoicesResponse.status === 400) {
            // No Bill.com customer ID, which we already checked for
            setInvoiceCount(0);
            setInvoiceTotal(0);
          } else {
            setError('Failed to fetch invoice summary');
          }
          return;
        }

        const data: InvoicesApiResponse = await invoicesResponse.json();
        const invoices = data.invoices?.results || [];

        setInvoiceCount(invoices.length);
        setInvoiceTotal(invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0));
      } catch (err) {
        setError('Failed to fetch invoice summary');
      } finally {
        setLoadingInvoices(false);
      }
    };

    fetchInvoiceSummary();
  }, [profile, isLoadingProfile, getAccessTokenSilently]);

  const loading = isLoadingProfile || loadingInvoices;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header / masthead */}
        <DashboardHero
          name={displayName || user?.name || ''}
          invoiceCount={invoiceCount}
          invoiceTotal={invoiceTotal}
          showInvoices={!error && invoiceCount > 0}
        />

        {/* Error Card */}
        {error && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="alert-danger">
              {error}
            </div>
          </div>
        )}

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Invoice Summary Card - Only show if there are invoices */}
          {!error && invoiceCount > 0 && (
            <div className="dag-kpi bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Invoices</h2>
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <DocumentTextIcon className="w-5 h-5 text-green-600" />
                </div>
              </div>
              
              <div className="mb-4">
                <div className="text-2xl font-bold text-gray-900">{invoiceCount}</div>
                <div className="text-sm text-gray-600">Total Invoices</div>
              </div>
              <div className="mb-4">
                <div className="text-2xl font-bold text-gray-900 dag-amount">{invoiceTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</div>
                <div className="text-sm text-gray-600">Total Amount</div>
              </div>
              <Link 
                to="/invoices" 
                className="link inline-flex items-center"
              >
                View Details
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          )}

          {/* Placeholder for future dashboard cards */}
          <div className="dag-kpi bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Coming Soon</h2>
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <PlusIcon className="w-5 h-5 text-gray-600" />
              </div>
            </div>
            <p className="text-gray-600 text-sm">More dashboard features coming soon...</p>
          </div>
        </div>
      </div>
  );
};

export default DashboardPage;